import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import { downloadBlob } from '../../utils/downloadBlob';
import { isoOf, rangeSlug, usDate } from './dateRange';
import {
  STATUS_META, currentReceipts, daysToSubmit, fmtCents, isLate, latestNote, LATE_AFTER_DAYS, purposeLine, receiptSummary,
} from './helpers';

/**
 * Expense report exports, laid out like the firm's expense report form: each
 * amount sits in Air & Trans, Lodging, Fuel/Mileage, Meals & Tips or Other,
 * grouped by employee with subtotals. buildReportModel() does all the grouping
 * and arithmetic (in integer cents); the two renderers only lay it out.
 */

export const FIRM_NAME = 'Halter Ferguson Financial, Inc.';

export const FORM_COLUMNS = [
  { key: 'air', label: 'Air & Trans' },
  { key: 'lodging', label: 'Lodging' },
  { key: 'mileage', label: 'Fuel/Mileage' },
  { key: 'meals', label: 'Meals & Tips' },
  { key: 'other', label: 'Other' },
];

const COLUMN_FOR_CATEGORY = { Travel: 'air', Lodging: 'lodging', Mileage: 'mileage', Meals: 'meals' };

/** The form column an expense's amount goes in. */
export const formColumnFor = (category) => COLUMN_FOR_CATEGORY[category] || 'other';

const zeroColumns = () => Object.fromEntries(FORM_COLUMNS.map((c) => [c.key, 0]));

const BREAKDOWN = [
  { status: 'approved', label: 'Approved, not yet paid' },
  { status: 'paid', label: 'Paid' },
  { status: 'pending', label: 'Pending review' },
  { status: 'rejected', label: 'Rejected' },
];

/** The local calendar date an ISO timestamp falls on. */
const localIso = (timestamp) => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? '' : isoOf(date);
};

/**
 * The report for these expenses, exactly as displayed.
 * options: { range, statusKey, statusLabel, employeeLabel, preparedBy, now }
 */
export const buildReportModel = (expenses, options = {}) => {
  const { range = { from: null, to: null, label: 'All expense dates' }, statusKey = 'all',
    statusLabel = 'All statuses', employeeLabel = 'All employees', preparedBy = '', now = new Date() } = options;

  const byEmployee = new Map();
  expenses.forEach((expense) => {
    if (!byEmployee.has(expense.user_id)) {
      byEmployee.set(expense.user_id, {
        userId: expense.user_id,
        name: expense.employee_name || expense.user_id,
        email: expense.employee_email || '',
        expenses: [],
      });
    }
    byEmployee.get(expense.user_id).expenses.push(expense);
  });

  const totals = zeroColumns();
  const groups = [...byEmployee.values()]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((group) => {
      const columns = zeroColumns();
      const rows = [...group.expenses]
        .sort((a, b) => a.expense_date.localeCompare(b.expense_date) || String(a.submitted_at).localeCompare(String(b.submitted_at)))
        .map((expense) => {
          const column = formColumnFor(expense.category);
          const cents = expense.amount_cents || 0;
          columns[column] += cents;
          totals[column] += cents;
          return {
            date: expense.expense_date,
            vendor: expense.merchant,
            purpose: purposeLine(expense),
            category: expense.category,
            status: expense.status,
            statusLabel: STATUS_META[expense.status]?.label || expense.status,
            submitted: localIso(expense.submitted_at),
            daysToSubmit: daysToSubmit(expense),
            late: isLate(expense),
            column,
            cents,
            note: latestNote(expense)?.note || '',
            receipts: receiptSummary(expense),
            noReceipt: currentReceipts(expense).length === 0,
          };
        });
      const totalCents = Object.values(columns).reduce((sum, value) => sum + value, 0);
      return { name: group.name, email: group.email, rows, columns, totalCents };
    });

  const totalCents = Object.values(totals).reduce((sum, value) => sum + value, 0);
  const breakdown = BREAKDOWN.map(({ status, label }) => {
    const matching = expenses.filter((e) => e.status === status);
    return { status, label, count: matching.length, cents: matching.reduce((sum, e) => sum + (e.amount_cents || 0), 0) };
  }).filter((line) => line.count > 0);
  const reimbursementOnly = expenses.length > 0 && expenses.every((e) => e.status === 'approved' || e.status === 'paid');

  return {
    firm: FIRM_NAME,
    title: 'Expense Report',
    meta: [
      `Prepared ${usDate(isoOf(now))}${preparedBy ? ` by ${preparedBy}` : ''}`,
      range.label,
      `Status: ${statusLabel}`,
      `Employee: ${employeeLabel}`,
    ],
    range,
    statusKey,
    employeeLabel,
    groups,
    totals,
    totalCents,
    totalLabel: reimbursementOnly ? 'Total reimbursement' : 'Total',
    // A mixed report says what the total is made of, so rejected or pending
    // amounts are never read as money owed.
    breakdown: reimbursementOnly && breakdown.length === 1 ? [] : breakdown,
    count: expenses.length,
    lateCount: groups.reduce((sum, g) => sum + g.rows.filter((r) => r.late).length, 0),
    hasMileage: expenses.some((e) => e.miles_tenths != null),
    noReceiptCount: expenses.filter((e) => currentReceipts(e).length === 0).length,
    signatures: groups.length === 1,
    signatureName: groups.length === 1 ? groups[0].name : '',
  };
};

const slug = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/** e.g. expense-report_2026-01-01_to_2026-11-02_approved_avery-sample.xlsx */
export const exportFilename = (model, ext) => {
  const parts = ['expense-report', rangeSlug(model.range), model.statusKey === 'all' ? 'all-statuses' : model.statusKey];
  if (model.signatures) parts.push(slug(model.signatureName));
  return `${parts.filter(Boolean).join('_')}.${ext}`;
};

const footnotes = (model) => [
  model.hasMileage && 'Mileage is priced at miles driven × the IRS business rate in effect on the expense date.',
  model.noReceiptCount > 0 &&
    `${model.noReceiptCount} ${model.noReceiptCount === 1 ? 'expense has' : 'expenses have'} no receipt attached; the reason given is shown with each.`,
  model.lateCount > 0 && `Late: submitted more than ${LATE_AFTER_DAYS} days after the expense date (the reimbursement policy asks for submission within ${LATE_AFTER_DAYS} days).`,
].filter(Boolean);

// ---------------------------------------------------------------------------
// Excel
// ---------------------------------------------------------------------------

const MONEY = '$#,##0.00';
const DATE = 'mm/dd/yyyy';
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7EEF7' } };
const GROUP_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F5F8' } };
const THIN = { style: 'thin', color: { argb: 'FFB8C2CF' } };

// Columns: A Date, B Vendor, C Business purpose, D Category, E Status, F Submitted,
// G-K the form's amount columns, L Total, M Receipts, N Notes.
const SHEET_COLUMNS = [
  { header: 'Date', width: 12 },
  { header: 'Vendor', width: 24 },
  { header: 'Business purpose', width: 46 },
  { header: 'Category', width: 20 },
  { header: 'Status', width: 11 },
  { header: 'Submitted', width: 18 },
  ...FORM_COLUMNS.map((c) => ({ header: c.label, width: 13 })),
  { header: 'Total', width: 13 },
  { header: 'Receipts', width: 34 },
  { header: 'Notes', width: 40 },
];
const FIRST_AMOUNT_COL = 7; // G
const TOTAL_COL = FIRST_AMOUNT_COL + FORM_COLUMNS.length; // L
const LAST_COL = SHEET_COLUMNS.length; // N
const letter = (col) => String.fromCharCode(64 + col);

/** An ISO date as an Excel date cell value (UTC midnight, so no day shift). */
const excelDate = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const dollars = (cents) => cents / 100;

export const buildExpenseWorkbook = (model) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = model.firm;
  workbook.title = model.title;
  const sheet = workbook.addWorksheet('Expense Report', {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  sheet.columns = SHEET_COLUMNS.map(({ width }) => ({ width }));

  const addTextRow = (text, font = {}) => {
    const row = sheet.addRow([text]);
    sheet.mergeCells(row.number, 1, row.number, LAST_COL);
    row.getCell(1).font = font;
    row.getCell(1).alignment = { wrapText: true, vertical: 'top' };
    return row;
  };

  addTextRow(model.firm, { bold: true, size: 14 });
  addTextRow(model.title, { bold: true, size: 12 });
  model.meta.forEach((line) => addTextRow(line, { color: { argb: 'FF4A5563' } }));
  sheet.addRow([]);

  const subtotalRows = [];
  model.groups.forEach((group) => {
    const heading = sheet.addRow([group.email ? `${group.name} (${group.email})` : group.name]);
    sheet.mergeCells(heading.number, 1, heading.number, LAST_COL);
    heading.getCell(1).font = { bold: true, size: 12 };
    heading.getCell(1).fill = GROUP_FILL;

    const header = sheet.addRow(SHEET_COLUMNS.map((c) => c.header));
    header.eachCell((cell, col) => {
      cell.font = { bold: true };
      cell.fill = HEADER_FILL;
      cell.border = { bottom: THIN };
      cell.alignment = { vertical: 'bottom', wrapText: true, horizontal: col >= FIRST_AMOUNT_COL && col <= TOTAL_COL ? 'right' : 'left' };
    });

    const firstRow = sheet.rowCount + 1;
    group.rows.forEach((item) => {
      const values = [
        excelDate(item.date), item.vendor, item.purpose, item.category, item.statusLabel,
        item.submitted ? excelDate(item.submitted) : '',
        ...FORM_COLUMNS.map((c) => (c.key === item.column ? dollars(item.cents) : null)),
        null,
        item.receipts,
        item.note,
      ];
      const row = sheet.addRow(values);
      row.getCell(1).numFmt = DATE;
      const submitted = row.getCell(6);
      submitted.numFmt = item.late ? `${DATE}" (late)"` : DATE;
      if (item.late) submitted.font = { color: { argb: 'FFC0392B' }, bold: true };
      for (let col = FIRST_AMOUNT_COL; col <= TOTAL_COL; col += 1) row.getCell(col).numFmt = MONEY;
      row.getCell(TOTAL_COL).value = {
        formula: `SUM(${letter(FIRST_AMOUNT_COL)}${row.number}:${letter(TOTAL_COL - 1)}${row.number})`,
        result: dollars(item.cents),
      };
      [3, LAST_COL - 1, LAST_COL].forEach((col) => { row.getCell(col).alignment = { wrapText: true, vertical: 'top' }; });
      if (item.noReceipt) row.getCell(LAST_COL - 1).font = { color: { argb: 'FFC0392B' } };
      row.alignment = { vertical: 'top' };
    });
    const lastRow = sheet.rowCount;

    const subtotal = sheet.addRow([]);
    subtotal.getCell(3).value = `Subtotal, ${group.name}`;
    subtotal.getCell(3).font = { bold: true };
    FORM_COLUMNS.forEach((c, i) => {
      const col = FIRST_AMOUNT_COL + i;
      subtotal.getCell(col).value = {
        formula: `SUM(${letter(col)}${firstRow}:${letter(col)}${lastRow})`,
        result: dollars(group.columns[c.key]),
      };
    });
    subtotal.getCell(TOTAL_COL).value = {
      formula: `SUM(${letter(TOTAL_COL)}${firstRow}:${letter(TOTAL_COL)}${lastRow})`,
      result: dollars(group.totalCents),
    };
    for (let col = FIRST_AMOUNT_COL; col <= TOTAL_COL; col += 1) {
      const cell = subtotal.getCell(col);
      cell.numFmt = MONEY;
      cell.font = { bold: true };
      cell.border = { top: THIN };
    }
    subtotalRows.push(subtotal.number);
    sheet.addRow([]);
  });

  const total = sheet.addRow([]);
  total.getCell(3).value = model.totalLabel;
  total.getCell(3).font = { bold: true, size: 12 };
  for (let col = FIRST_AMOUNT_COL; col <= TOTAL_COL; col += 1) {
    const key = FORM_COLUMNS[col - FIRST_AMOUNT_COL]?.key;
    const cell = total.getCell(col);
    cell.value = {
      formula: subtotalRows.length ? subtotalRows.map((r) => `${letter(col)}${r}`).join('+') : '0',
      result: dollars(key ? model.totals[key] : model.totalCents),
    };
    cell.numFmt = MONEY;
    cell.font = { bold: true, size: 12 };
    cell.border = { top: THIN, bottom: { style: 'double', color: { argb: 'FF1B2330' } } };
  }

  if (model.breakdown.length) {
    sheet.addRow([]);
    model.breakdown.forEach((line) => {
      const row = sheet.addRow([]);
      row.getCell(3).value = `${line.label} (${line.count})`;
      row.getCell(TOTAL_COL).value = dollars(line.cents);
      row.getCell(TOTAL_COL).numFmt = MONEY;
    });
  }

  const notes = footnotes(model);
  if (notes.length) {
    sheet.addRow([]);
    notes.forEach((note) => addTextRow(note, { italic: true, color: { argb: 'FF4A5563' } }));
  }

  if (model.signatures) {
    sheet.addRow([]);
    sheet.addRow([]);
    [`Employee's signature (${model.signatureName})`, "Supervisor's signature"].forEach((label) => {
      const row = sheet.addRow([]);
      row.getCell(2).value = label;
      row.getCell(3).border = { bottom: THIN };
      row.getCell(5).value = 'Date';
      row.getCell(6).border = { bottom: THIN };
      sheet.addRow([]);
    });
  }

  return workbook;
};

// ---------------------------------------------------------------------------
// PDF
// ---------------------------------------------------------------------------

const PAGE = { width: 792, height: 612, margin: 36 };
const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;
const INK = [27, 35, 48];
const MUTED = [91, 101, 116];
const RULE = [184, 194, 207];
const FILL = [231, 238, 247];
const LATE_RED = [192, 57, 43];
const AMOUNT_WIDTH = 46;
const PDF_COLUMNS = [
  { key: 'date', label: 'Date', width: 50 },
  { key: 'vendor', label: 'Vendor', width: 88 },
  { key: 'purpose', label: 'Business purpose', width: 170 },
  // Wide enough for "Conference/Training" on one line.
  { key: 'category', label: 'Category', width: 78 },
  { key: 'status', label: 'Status', width: 44 },
  { key: 'submitted', label: 'Submitted', width: 60 },
  // Break two-word labels at the slash rather than mid-word.
  ...FORM_COLUMNS.map((c) => ({ key: c.key, label: c.label.replace('/', '/\n'), width: AMOUNT_WIDTH, amount: true })),
];
const FONT_SIZE = 7.5;
const LINE = 9;
const CELL_PAD = 3;

// The built-in PDF fonts cover Windows-1252 only; map the few other characters
// the page uses and drop anything else rather than print garbage.
const pdfText = (value) =>
  String(value ?? '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/\u00D7/g, 'x')
    .replace(/[^\t\n\r\u0020-\u007E\u00A0-\u00FF]/g, '');

class ReportPdf {
  constructor() {
    this.doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    this.y = PAGE.margin;
  }

  color(rgb, kind = 'text') {
    if (kind === 'text') this.doc.setTextColor(...rgb);
    else if (kind === 'fill') this.doc.setFillColor(...rgb);
    else this.doc.setDrawColor(...rgb);
  }

  font(size, style = 'normal', rgb = INK) {
    this.doc.setFont('helvetica', style);
    this.doc.setFontSize(size);
    this.color(rgb);
  }

  /** Start a new page when `height` won't fit; `onNewPage` redraws a table header. */
  room(height, onNewPage) {
    if (this.y + height <= PAGE.height - PAGE.margin - 14) return;
    this.doc.addPage();
    this.y = PAGE.margin;
    if (onNewPage) onNewPage();
  }

  line(text, size, style, rgb, gap = 4) {
    this.font(size, style, rgb);
    const lines = this.doc.splitTextToSize(pdfText(text), CONTENT_WIDTH);
    this.room(lines.length * size * 1.2);
    this.doc.text(lines, PAGE.margin, this.y + size);
    this.y += lines.length * size * 1.2 + gap;
  }

  tableHeader() {
    const height = LINE * 2 + CELL_PAD * 2;
    this.color(FILL, 'fill');
    this.doc.rect(PAGE.margin, this.y, CONTENT_WIDTH, height, 'F');
    this.font(FONT_SIZE, 'bold');
    let x = PAGE.margin;
    PDF_COLUMNS.forEach((col) => {
      const lines = this.doc.splitTextToSize(col.label, col.width - CELL_PAD * 2);
      const textX = col.amount ? x + col.width - CELL_PAD : x + CELL_PAD;
      this.doc.text(lines, textX, this.y + CELL_PAD + FONT_SIZE, { align: col.amount ? 'right' : 'left' });
      x += col.width;
    });
    this.y += height;
  }

  groupHeading(group, continued = false) {
    this.font(10, 'bold');
    const label = `${group.name}${group.email ? ` (${group.email})` : ''}${continued ? ' (continued)' : ''}`;
    this.doc.text(pdfText(label), PAGE.margin, this.y + 10);
    this.y += 16;
  }

  row(item, group) {
    const cells = {
      date: usDate(item.date),
      vendor: item.vendor,
      purpose: item.purpose,
      category: item.category,
      status: item.statusLabel,
      submitted: item.submitted ? `${usDate(item.submitted)}${item.late ? '\nLate' : ''}` : '',
      [item.column]: fmtCents(item.cents),
    };
    this.font(FONT_SIZE);
    const wrapped = PDF_COLUMNS.map((col) =>
      this.doc.splitTextToSize(pdfText(cells[col.key] || ''), col.width - CELL_PAD * 2));
    // Receipts, then the latest note, as indented lines under the row.
    const extra = [`Receipts: ${item.receipts}`, item.note && `Note: ${item.note}`].filter(Boolean).join('\n');
    const noteLines = this.doc.splitTextToSize(pdfText(extra), CONTENT_WIDTH - PDF_COLUMNS[0].width - CELL_PAD * 2);
    const height = Math.max(...wrapped.map((l) => l.length)) * LINE + noteLines.length * LINE + CELL_PAD * 2;
    this.room(height, () => { this.groupHeading(group, true); this.tableHeader(); });

    let x = PAGE.margin;
    PDF_COLUMNS.forEach((col, i) => {
      const late = col.key === 'submitted' && item.late;
      this.font(FONT_SIZE, late ? 'bold' : 'normal', late ? LATE_RED : INK);
      const textX = col.amount ? x + col.width - CELL_PAD : x + CELL_PAD;
      this.doc.text(wrapped[i], textX, this.y + CELL_PAD + FONT_SIZE, { align: col.amount ? 'right' : 'left' });
      x += col.width;
    });
    if (noteLines.length) {
      this.font(FONT_SIZE, 'italic', MUTED);
      const top = this.y + CELL_PAD + FONT_SIZE + Math.max(...wrapped.map((l) => l.length)) * LINE;
      this.doc.text(noteLines, PAGE.margin + PDF_COLUMNS[0].width + CELL_PAD, top);
    }
    this.y += height;
    this.color(RULE, 'draw');
    this.doc.setLineWidth(0.3);
    this.doc.line(PAGE.margin, this.y, PAGE.margin + CONTENT_WIDTH, this.y);
  }

  amountsRow(label, columns, totalCents, { size = FONT_SIZE, double = false } = {}) {
    this.room(LINE * 2 + CELL_PAD * 2);
    this.font(size, 'bold');
    const labelX = PAGE.margin + PDF_COLUMNS[0].width + PDF_COLUMNS[1].width + CELL_PAD;
    this.doc.text(pdfText(`${label}: ${fmtCents(totalCents)}`), labelX, this.y + CELL_PAD + size);
    let x = PAGE.margin + PDF_COLUMNS.filter((c) => !c.amount).reduce((sum, c) => sum + c.width, 0);
    FORM_COLUMNS.forEach((c) => {
      this.doc.text(fmtCents(columns[c.key]), x + AMOUNT_WIDTH - CELL_PAD, this.y + CELL_PAD + size, { align: 'right' });
      x += AMOUNT_WIDTH;
    });
    this.y += size + CELL_PAD * 2 + 2;
    if (double) {
      this.color(INK, 'draw');
      this.doc.setLineWidth(0.6);
      this.doc.line(PAGE.margin, this.y, PAGE.margin + CONTENT_WIDTH, this.y);
      this.doc.line(PAGE.margin, this.y + 2, PAGE.margin + CONTENT_WIDTH, this.y + 2);
      this.y += 6;
    }
  }

  signatures(name) {
    this.room(90);
    this.y += 24;
    this.color(INK, 'draw');
    this.doc.setLineWidth(0.6);
    [`Employee's signature (${name})`, "Supervisor's signature"].forEach((label) => {
      this.doc.line(PAGE.margin, this.y, PAGE.margin + 300, this.y);
      this.doc.line(PAGE.margin + 340, this.y, PAGE.margin + 480, this.y);
      this.font(8, 'normal', MUTED);
      this.doc.text(pdfText(label), PAGE.margin, this.y + 10);
      this.doc.text('Date', PAGE.margin + 340, this.y + 10);
      this.y += 36;
    });
  }

  pageNumbers() {
    const pages = this.doc.getNumberOfPages();
    for (let page = 1; page <= pages; page += 1) {
      this.doc.setPage(page);
      this.font(7, 'normal', MUTED);
      this.doc.text(`${FIRM_NAME} expense report`, PAGE.margin, PAGE.height - PAGE.margin + 10);
      this.doc.text(`Page ${page} of ${pages}`, PAGE.width - PAGE.margin, PAGE.height - PAGE.margin + 10, { align: 'right' });
    }
  }
}

export const buildExpensePdf = (model) => {
  const pdf = new ReportPdf();
  pdf.line(model.firm, 14, 'bold', INK, 2);
  pdf.line(model.title, 12, 'bold', INK, 6);
  model.meta.forEach((line) => pdf.line(line, 9, 'normal', MUTED, 1));
  pdf.y += 10;

  if (!model.groups.length) pdf.line('No expenses match these filters.', 10, 'italic', MUTED);
  model.groups.forEach((group) => {
    pdf.room(16 + LINE * 2 + CELL_PAD * 2 + LINE * 2);
    pdf.groupHeading(group);
    pdf.tableHeader();
    group.rows.forEach((item) => pdf.row(item, group));
    pdf.amountsRow(`Subtotal, ${group.name}`, group.columns, group.totalCents);
    pdf.y += 10;
  });
  pdf.amountsRow(model.totalLabel, model.totals, model.totalCents, { size: 9, double: true });
  model.breakdown.forEach((line) => pdf.line(`${line.label} (${line.count}): ${fmtCents(line.cents)}`, 8, 'normal', INK, 1));
  if (model.breakdown.length) pdf.y += 6;
  footnotes(model).forEach((note) => pdf.line(note, 7.5, 'italic', MUTED, 2));
  if (model.signatures) pdf.signatures(model.signatureName);
  pdf.pageNumbers();
  return pdf.doc;
};

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Build the report for `expenses` and download it as 'xlsx' or 'pdf'. */
export const exportExpenses = async (expenses, options, format) => {
  const model = buildReportModel(expenses, options);
  if (format === 'xlsx') {
    const buffer = await buildExpenseWorkbook(model).xlsx.writeBuffer();
    downloadBlob(new Blob([buffer], { type: XLSX_TYPE }), exportFilename(model, 'xlsx'));
  } else {
    downloadBlob(buildExpensePdf(model).output('blob'), exportFilename(model, 'pdf'));
  }
  return model;
};
